/**
 * 文件存储 API（对齐 FileController，common-storage）。
 *
 * 真实端点（网关 /api/file/** → blu-trade /file/**，StripPrefix=1）：
 * - POST /file/upload   multipart 上传，返回 FileAttachment（其 id 即后续 fileId）
 * - GET  /file/list     按 bizType/bizId/bizId2/bizId3 查询文件列表（对齐后台 listFilesByBiz）
 * - GET  /file/{id}/url 取文件预览 URL（对齐后台 getFileUrl）
 * - DELETE /file/{id}   删除文件（对齐后台 deleteFile）
 *
 * 两步上传：先传字节拿 fileId，再关联（子步骤证据走 uploadSubstepDocument / 节点单证走 advance.files）。
 */
import { request, uploadFile } from './request';

export interface FileAttachment {
  id: string;
  fileName?: string;
  url?: string;
  [key: string]: unknown;
}

export interface UploadMeta {
  bizId?: string;
  bizId2?: string;
  bizId3?: string;
}

export function uploadFileToServer(
  file: Blob,
  bizType: string,
  meta?: UploadMeta,
): Promise<FileAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('bizType', bizType);
  if (meta?.bizId) formData.append('bizId', meta.bizId);
  if (meta?.bizId2) formData.append('bizId2', meta.bizId2);
  if (meta?.bizId3) formData.append('bizId3', meta.bizId3);
  return uploadFile<FileAttachment>('/file/upload', formData);
}

/** 按业务维度查文件列表（对齐后台 listFilesByBiz；bizType=node_doc 用于子步骤附件回显） */
export function listFilesByBiz(
  bizType: string,
  bizId: string,
  bizId2?: string,
  bizId3?: string,
): Promise<FileAttachment[]> {
  const params: Record<string, string> = { bizType, bizId };
  if (bizId2) params.bizId2 = bizId2;
  if (bizId3) params.bizId3 = bizId3;
  return request<FileAttachment[]>('/file/list', { params });
}

/** 取文件预览 URL（对齐后台 getFileUrl） */
export function getFileUrl(
  fileId: string,
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
): Promise<string> {
  const params: Record<string, string> = {};
  if (host) params.host = host;
  return request<string>(`/file/${fileId}/url`, { params });
}

/** 取文件在线预览 URL（inline，不带 Content-Disposition: attachment，适合 iframe 内嵌 PDF） */
export function getFileInlineUrl(
  fileId: string,
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
): Promise<string> {
  const params: Record<string, string> = {};
  if (host) params.host = host;
  return request<string>(`/file/${fileId}/inline-url`, { params });
}

/** 删除文件（对齐后台 deleteFile） */
export function deleteFile(fileId: string): Promise<boolean> {
  return request<boolean>(`/file/${fileId}`, { method: 'DELETE' });
}
